"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { COOKIE_VERSION, STORAGE_KEYS, type CookieConsent } from "../constants";
import { showToast } from "../toast";

function isConsent(value: unknown): value is CookieConsent {
  return (
    typeof value === "object" &&
    value !== null &&
    "necessary" in value &&
    "analytics" in value &&
    "version" in value
  );
}

function readConsent(): CookieConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.cookieConsent);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isConsent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const acceptRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setVisible(readConsent() === null);
  }, []);

  const save = useCallback((analytics: boolean) => {
    const payload: CookieConsent = {
      necessary: true,
      analytics,
      marketing: false,
      version: COOKIE_VERSION,
      date: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEYS.cookieConsent, JSON.stringify(payload));
    } catch {
      /* приватный режим */
    }
    // сообщаем аналитике (Яндекс.Метрика) об изменении согласия без перезагрузки
    window.dispatchEvent(new Event("slot-cookie-consent-changed"));
    setVisible(false);
  }, []);

  useEffect(() => {
    const onSettingsClick = (event: Event) => {
      if (!(event.target instanceof Element) || !event.target.closest("[data-cookie-settings]")) return;
      event.preventDefault();
      try {
        window.localStorage.removeItem(STORAGE_KEYS.cookieConsent);
      } catch {
        /* noop */
      }
      setVisible(true);
      acceptRef.current?.focus();
    };
    document.addEventListener("click", onSettingsClick);
    return () => document.removeEventListener("click", onSettingsClick);
  }, []);

  if (!visible) return null;

  return (
    <section
      className="cookie"
      id="cookie-banner"
      data-cookie-banner
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-title">
      <div className="cookie__inner">
        <div className="cookie__text">
          <h2 className="cookie__title" id="cookie-title">
            Cookie
          </h2>
          <p>
            Мы используем необходимые cookie для работы сервиса. Аналитические cookie — только с вашего
            согласия. Подробнее — в{" "}
            <a href="/cookie-policy">политике cookie</a> и{" "}
            <a href="/privacy">политике обработки ПДн</a>.
          </p>
        </div>
        <div className="cookie__actions">
          <button
            ref={acceptRef}
            type="button"
            className="btn btn--primary btn--sm"
            data-cookie-accept
            onClick={() => {
              save(true);
              showToast("Спасибо. Аналитические cookie включены.");
            }}>
            Принять все
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            data-cookie-reject
            onClick={() => {
              save(false);
              showToast("Только необходимые cookie. Доступ к сайту не ограничен.");
            }}>
            Только необходимые
          </button>
        </div>
      </div>
    </section>
  );
}
